import type { ReactNode } from "react";
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";

import type { Route } from "./+types/root";
import { createDataLoaders, dbContext } from "./utils/request/context.server.ts";

import "./index.css";

export const middleware: Route.MiddlewareFunction[] = [
	async ({ context }, next) => {
		context.set(dbContext, createDataLoaders());

		return next();
	},
];

export function Layout({ children }: { children: ReactNode }) {
	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				{/* `interactive-widget=resizes-content` makes Chromium shrink the layout
				    viewport when the virtual keyboard opens, so `dvh` accounts for it with
				    no JavaScript. WebKit has not implemented it (bug 259770), which is why
				    the shell also derives its height from `visualViewport`.
				    `viewport-fit=cover` is what makes `env(safe-area-inset-*)` resolve to
				    anything other than zero, so edge-pinned controls can clear the home
				    indicator instead of guessing at a fixed padding. */}
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content"
				/>
				<title>Clearfor</title>
				<Meta />
				<Links />
			</head>
			<body>
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export default function App() {
	return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	let message = "Something went wrong";
	let details = "An unexpected error occurred.";

	if (isRouteErrorResponse(error)) {
		message = error.status === 404 ? "Not found" : `Error ${error.status}`;
		details = error.status === 404 ? "The page you are looking for does not exist." : error.statusText || details;
	} else if (import.meta.env.DEV && error instanceof Error) {
		details = error.message;
	}

	return (
		<main style={{ padding: "4rem", fontFamily: "sans-serif" }}>
			<h1>{message}</h1>
			<p>{details}</p>
		</main>
	);
}
